/**
 * Selleasy DOM corrections (E20-1483)
 *
 * Two things the stylesheet can't reach in the vendor's own markup: the money format, and
 * footnote markers that stay keyboard-operable.
 *
 * Every price the theme renders goes through Liquid's `money_without_trailing_zeros`, so the
 * site shows "$999", not "$999.00" (see snippets/bfx-promo-price.liquid). The Selleasy bundle
 * widget builds its own markup client-side from the app's API and formats its own money, so no
 * Liquid filter reaches it — the widget showed "$1,099.00" directly beneath a buy box showing
 * "$999", which is the sort of mismatch the shopper reads as two different prices.
 *
 * This can't be done in CSS: the amount is a single text node, and CSS cannot alter text.
 *
 * The widget re-renders on its own (ticking the add-on recalculates the total, and opening the
 * quick view builds a fresh <dialog>), so the correction has to re-apply rather than run once.
 * Rewriting a price replaces its text node, which wakes the observer again — the pass is
 * idempotent, so the second wake-up matches nothing and it settles immediately.
 *
 * Scoped to Selleasy's own containers. If the app is ever replaced (E20-1356) this file goes
 * with it, along with scss/components/_selleasy.scss.
 */
const initSelleasyFixes = () => {
  // The app block ships in the server-rendered HTML even before the app hydrates into it; the
  // quick view is a <dialog> appended to <body> the first time a card is opened.
  const WIDGET = '.lb-widget-ba';
  const PRICE = '.money, .lb-price-discount';

  // Footnote markers carried in from the product description, which anchor to #footnotes further
  // down the PDP.
  const FOOTNOTE = '.lb-product-info-description a[href^="#"]';

  // A trailing zero-cents on the amount, with any currency suffix left alone: "$1,099.00" →
  // "$1,099", "-$349.00" → "-$349", "$1,099.00 USD" → "$1,099 USD". Anything with real cents is
  // untouched, and a thousands separator can't match because digits follow it.
  // Both separators are accepted: fr-CA isn't live yet, but it is a supported locale and formats
  // the same amount as "1 099,00 $".
  const TRAILING_ZEROS = /(\d)[.,]00(?=\D*$)/;

  const trim = (el) => {
    const text = el.textContent;
    if (!TRAILING_ZEROS.test(text)) return;
    const next = text.replace(TRAILING_ZEROS, '$1');
    // Guard the write as well as the test: an unchanged assignment would still replace the text
    // node and wake the observer for nothing.
    if (next !== text) el.textContent = next;
  };

  // scss/components/_selleasy.scss makes these markers look inert, but `pointer-events: none`
  // only blocks mouse and touch — they keep tabindex 0, so a keyboard user can still Tab to one
  // inside the dialog and press Enter, which follows the hash and scrolls the page behind it.
  // Dropping the href is what actually makes them inert: an <a> without one is neither focusable
  // nor a link, and the marker still reads as a marker.
  const neuter = (anchor) => {
    if (!anchor.hasAttribute('href')) return;
    anchor.removeAttribute('href');
  };

  const fixTree = (node) => {
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    if (node.matches(PRICE)) trim(node);
    node.querySelectorAll(PRICE).forEach(trim);
    if (node.matches(FOOTNOTE)) neuter(node);
    node.querySelectorAll(FOOTNOTE).forEach(neuter);
  };

  // Re-checks only the mutated subtree rather than re-scanning the whole widget on every wake-up.
  const watch = (root) => {
    fixTree(root);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'characterData') {
          const parent = mutation.target.parentElement;
          if (parent) fixTree(parent);
        } else {
          mutation.addedNodes.forEach(fixTree);
        }
      });
    });

    observer.observe(root, { childList: true, subtree: true, characterData: true });
  };

  document.querySelectorAll(WIDGET).forEach(watch);

  // The quick-view dialog is created on demand, so wait for it rather than assuming it exists.
  // Only direct children of <body> are inspected — the dialog is appended there — which keeps
  // this off the hot path for the rest of the page's own DOM churn.
  const bodyObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        if (node.tagName === 'DIALOG' && node.querySelector('.lb-widget, lb-upsell-dialog'))
          watch(node);
      });
    });
  });

  bodyObserver.observe(document.body, { childList: true });
};

initSelleasyFixes();
